import React from 'react';
import { supabase } from '../../supabase';
import { isRenderMode, renderModeCortex } from '../../renderMode';

// ─── Constants ───────────────────────────────────────────────────────────────

export const CORTEX_PAGE_FETCH = 500;
export const CORTEX_SKU_COLORWAYS = 250;
export const CORTEX_SKU_ITEMS_PER_CW = 100;
export const PAGE_SIZE = 25;
export const STOCK_RED = '#C1392B';

export const POOLS = [
  { key: 'geodis_sellable', label: 'Geodis', sub: 'Sellable' },
  { key: 'stores', label: 'Stores', sub: 'Sellable' },
  { key: 'geodis_ns', label: 'Geodis', sub: 'Non-sellable' },
  { key: 'stores_ns', label: 'Stores', sub: 'Non-sellable' },
];

// ─── Cortex query helper ─────────────────────────────────────────────────────

async function getFreshAccessToken(forceRefresh = false) {
  if (forceRefresh) {
    const { data } = await supabase.auth.refreshSession();
    if (data?.session?.access_token) return data.session.access_token;
  }
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

export async function cortexQuery(graphql, { signal, retries = 2 } = {}) {
  // Export pipeline: serve Cortex reads from the injected bundle (no auth/network).
  if (isRenderMode) return renderModeCortex(graphql);

  let token = await getFreshAccessToken();
  if (!token) throw new Error('Not authenticated');

  const backoffs = [200, 500, 1200];
  let lastErr;
  let refreshed = false;
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (signal?.aborted) {
      const err = new Error('Aborted');
      err.name = 'AbortError';
      throw err;
    }
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cortex-query`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mode: 'graphql', question: graphql }),
        signal,
      });
      // 401 → session likely expired; refresh once and retry
      if (res.status === 401 && !refreshed) {
        refreshed = true;
        const newToken = await getFreshAccessToken(true);
        if (newToken) {
          token = newToken;
          continue; // retry with fresh token (doesn't count against retries)
        }
        throw new Error('Session expired — please refresh the page and sign in again.');
      }
      const json = await res.json();
      // Retry only on 5xx / network hiccups, not on 4xx or GraphQL errors
      if (res.status >= 500 && attempt < retries) {
        await new Promise(r => setTimeout(r, backoffs[attempt] ?? 1200));
        continue;
      }
      if (!res.ok || json.errors) {
        const msg = json.errors?.[0]?.message || json.error || `Cortex error (${res.status})`;
        throw new Error(msg);
      }
      return json.data;
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      lastErr = err;
      // Retry only for network-level failures (TypeError typically from fetch)
      if (attempt < retries && (err instanceof TypeError)) {
        await new Promise(r => setTimeout(r, backoffs[attempt] ?? 1200));
        continue;
      }
      throw err;
    }
  }
  throw lastErr || new Error('Cortex query failed');
}

// Search matches SKU (contains) OR product name (resolved to matching names by
// the caller and passed via `productNameMatches`). Cortex InventoryFilter is
// AND-only, so when product names match we swap the sku-contains filter for a
// productNames IN filter. SKU contains is the fallback when nothing resolves.
export function buildInventoryFilter(filters, search, extras = [], productNameMatches = null) {
  const f = [];
  if (filters.location?.length) f.push(`locations: ${JSON.stringify(filters.location)}`);
  if (filters.department?.length) f.push(`departments: ${JSON.stringify(filters.department)}`);
  if (filters.division?.length) f.push(`divisions: ${JSON.stringify(filters.division)}`);
  if (filters.gender?.length) f.push(`genders: [${filters.gender.join(', ')}]`);
  if (filters.productName) f.push(`productNames: ${JSON.stringify([filters.productName])}`);
  if (filters.colorwayId) f.push(`colorwayIds: ${JSON.stringify([filters.colorwayId])}`);
  if (search) {
    if (productNameMatches && productNameMatches.length > 0) {
      f.push(`productNames: ${JSON.stringify(productNameMatches)}`);
    } else {
      f.push(`sku: { contains: ${JSON.stringify(search)} }`);
    }
  }
  f.push(...extras);
  return f.length ? `filter: { ${f.join(', ')} }` : '';
}

// ─── Pool classification ─────────────────────────────────────────────────────

export function classifyLocationPool(name) {
  if (!name) return 'unknown';
  if (name.startsWith('Damages ')) return 'stores_ns';
  if (/^FG\s*[-–—]/.test(name)) return 'geodis_sellable';
  if (name.startsWith('Geodis ')) return 'geodis_ns';
  if (/^\d{3}\s*[-–—]/.test(name)) return 'stores';
  return 'unknown';
}

// ─── Stock state → color ─────────────────────────────────────────────────────

export function stockStateColor(stocked, total, B) {
  if (!total) return B.stone;
  if (stocked === 0) return STOCK_RED;
  if (stocked >= total) return B.olive;
  return B.rust;
}

// ─── CC-level inventory fetchers (replace rpc('get_inventory_for_cc')) ──────

// Returns the 3-bucket shape the old RPC returned:
// { totalOnHand, totalCommitted, totalAvailable,
//   dc: {onHand, committed, available},
//   store: {onHand, committed, available, count},
//   onhold: {onHand, committed, available},
//   byFGLocation: { [channelNameStrippedOfFGPrefix]: {onHand, committed, available} } }
// - dc   = all FG - pools (Ecomm / Retail / Wholesale / Corp Sales / Marketing)
// - store = numbered retail stores (001-, 602-)
// - onhold = Geodis non-sellable locations whose name contains "Hold" or "QC"
export async function fetchCcInventory(ccCode, { signal } = {}) {
  if (!ccCode) return null;
  const data = await cortexQuery(`{
    inventory(groupBy: LOCATION, first: 500, filter: { colorwayIds: ${JSON.stringify([ccCode])} }) {
      totals { onHandUnits availableUnits }
      breakdowns {
        dimensions { key label }
        metrics { onHandUnits availableUnits }
      }
    }
  }`, { signal });

  const totals = data?.inventory?.totals || { onHandUnits: 0, availableUnits: 0 };
  const breakdowns = data?.inventory?.breakdowns || [];
  const result = {
    totalOnHand: totals.onHandUnits || 0,
    totalCommitted: (totals.onHandUnits || 0) - (totals.availableUnits || 0),
    totalAvailable: totals.availableUnits || 0,
    dc: { onHand: 0, committed: 0, available: 0 },
    store: { onHand: 0, committed: 0, available: 0, count: 0 },
    onhold: { onHand: 0, committed: 0, available: 0 },
    byFGLocation: {},
  };
  for (const b of breakdowns) {
    const name = b.dimensions[0]?.label || b.dimensions[0]?.key || '';
    const pool = classifyLocationPool(name);
    const oh = b.metrics.onHandUnits || 0;
    const av = b.metrics.availableUnits || 0;
    const cm = oh - av;
    if (pool === 'geodis_sellable') {
      result.dc.onHand += oh; result.dc.committed += cm; result.dc.available += av;
      const key = name.replace(/^FG - /, '');
      result.byFGLocation[key] = { onHand: oh, committed: cm, available: av };
    } else if (pool === 'stores') {
      result.store.onHand += oh; result.store.committed += cm; result.store.available += av;
      result.store.count += 1;
    } else if (pool === 'geodis_ns' && /\b(hold|qc)\b/i.test(name)) {
      result.onhold.onHand += oh; result.onhold.committed += cm; result.onhold.available += av;
    }
    // Damages + non-OnHold Geodis (B/C/D Grade) are excluded, matching old RPC behavior
  }
  return result;
}

// ─── Cortex attribute map (site-wide cache) ──────────────────────────────────
// Maps colorway id → product attribution data (category, material, etc.).
// Used by The Exchange to enrich sales/returns data without a DB sync.

// Cortex Gender enum → display label, in display order. The attribute map stores the
// label; The Exchange maps it back to the enum for SalesFilter `genders`.
export const GENDER_DISPLAY = { MENS: "Men's", WOMENS: "Women's", KIDS: 'Kids', UNISEX: 'Unisex' };
export const genderLabel = (gender) => (gender ? (GENDER_DISPLAY[gender] ?? gender) : null);

let _cortexAttrMap = null;
let _cortexAttrLoadPromise = null;

export async function fetchCortexAttributeMap() {
  // App-wide singleton: deliberately ignores caller abort signals — a StrictMode
  // first-mount abort used to cancel the shared load and cache an EMPTY map for
  // the whole session (all products 'Unknown'/'-', search matching nothing).
  if (_cortexAttrMap && _cortexAttrMap.size > 0) return _cortexAttrMap;
  if (_cortexAttrLoadPromise) return _cortexAttrLoadPromise;
  _cortexAttrLoadPromise = (async () => {
    const map = new Map();
    try {
      let after = null;
      let withProductName = true; // falls back if the schema lacks Product.name
      for (let i = 0; i < 10; i++) {
        const afterArg = after ? `, after: "${after}"` : '';
        const productFields = `${withProductName ? 'name ' : ''}category productCategory gender silhouette toeShape department franchise`;
        let data;
        try {
          data = await cortexQuery(
            `{ colorways(first: 1000${afterArg}) {
              nodes {
                id name color material
                product { ${productFields} }
              }
              pageInfo { hasNextPage endCursor }
            } }`
          );
        } catch (err) {
          if (withProductName && i === 0 && /name/i.test(err?.message || '')) {
            console.warn('Cortex attr map: Product.name unavailable — retrying without it');
            withProductName = false;
            i -= 1; // redo this page
            continue;
          }
          throw err;
        }
        const nodes = data?.colorways?.nodes || [];
        for (const n of nodes) {
          if (!n?.id) continue;
          map.set(n.id, {
            name: n.name ?? null,
            color: n.color ?? null,
            productName: n.product?.name ?? null,
            category: n.product?.category ?? null,
            material: n.material ?? null,
            productCategory: n.product?.productCategory ?? null,
            gender: genderLabel(n.product?.gender),
            silhouette: n.product?.silhouette ?? null,
            toeShape: n.product?.toeShape ?? null,
            department: n.product?.department ?? null,
            franchise: n.product?.franchise ?? null,
          });
        }
        if (!data?.colorways?.pageInfo?.hasNextPage) break;
        after = data.colorways.pageInfo.endCursor;
      }
    } catch (e) {
      if (e.name !== 'AbortError') console.error('Cortex attribute map load failed:', e);
    }
    if (map.size > 0) _cortexAttrMap = map; // never cache an empty map
    _cortexAttrLoadPromise = null;
    return map;
  })();
  return _cortexAttrLoadPromise;
}

// ─── Cortex colorway cache (site-wide) ───────────────────────────────────────
// Single-load maps from colorway id → image URL and colorway id → catalog variant
// count. Consumers can call fetchCortexImageMap multiple times — fetches once,
// subsequent calls return the in-memory maps immediately. Variant count lets the
// Launch Line Up tile compute SKU coverage against the real Cortex catalog (same
// denominator the modal uses), so tile dots and modal boots stay in sync.
let _cortexImageMap = null;
let _cortexVariantCountMap = null;
let _cortexImageLoadPromise = null;

export async function fetchCortexImageMap({ signal } = {}) {
  if (_cortexImageMap) return _cortexImageMap;
  if (_cortexImageLoadPromise) return _cortexImageLoadPromise;
  _cortexImageLoadPromise = (async () => {
    const imgMap = new Map();
    const variantMap = new Map();
    try {
      let after = null;
      // 30-page ceiling (was 10): the catalog outgrew 5,000 colorways and CCs
      // past the cap silently lost their images (ZM10 finding, 2026-08-12).
      // Loop still exits at hasNextPage=false, so small catalogs pay nothing.
      for (let i = 0; i < 30; i++) {
        const afterArg = after ? `, after: "${after}"` : '';
        const data = await cortexQuery(
          `{ colorways(first: 500${afterArg}) { nodes { id image { url } variants { sku } } pageInfo { hasNextPage endCursor } } }`,
          { signal }
        );
        const nodes = data?.colorways?.nodes || [];
        for (const n of nodes) {
          if (!n?.id) continue;
          if (n?.image?.url) imgMap.set(n.id, n.image.url);
          if (Array.isArray(n.variants)) variantMap.set(n.id, n.variants.length);
        }
        if (!data?.colorways?.pageInfo?.hasNextPage) break;
        after = data.colorways.pageInfo.endCursor;
      }
    } catch (e) {
      if (e.name !== 'AbortError') console.error('Cortex colorway map load failed:', e);
    }
    _cortexImageMap = imgMap;
    _cortexVariantCountMap = variantMap;
    _cortexImageLoadPromise = null;
    return imgMap;
  })();
  return _cortexImageLoadPromise;
}

export function getCortexImageSync(cc) {
  if (!_cortexImageMap || !cc) return null;
  return _cortexImageMap.get(cc) || null;
}

export function getCortexVariantCountSync(cc) {
  if (!_cortexVariantCountMap || !cc) return 0;
  return _cortexVariantCountMap.get(cc) || 0;
}

// Rich per-bucket SKU coverage. Returns:
// { expectedCount, Ecomm, Retail, Wholesale, onHold, stores }
// where each bucket is the count of distinct SKUs with available > 0 in that bucket.
// Use inventoryStatusColor(bucketCount, expectedCount, B) to derive a boot color.
export async function fetchCcCoverage(ccCode, { signal } = {}) {
  if (!ccCode) return { expectedCount: 0, Ecomm: 0, Retail: 0, Wholesale: 0, onHold: 0, stores: 0 };
  const data = await cortexQuery(`{
    cw: colorway(by: { id: ${JSON.stringify(ccCode)} }) { variants { sku } }
    inv: inventory(groupBy: LOCATION, first: 500, filter: { colorwayIds: ${JSON.stringify([ccCode])} }) {
      breakdowns {
        dimensions { key label }
        items(first: 500) { nodes { sku availableUnits } }
      }
    }
  }`, { signal });

  const expectedCount = (data?.cw?.variants || []).length;
  const stockedByLoc = {}; // locName -> Set of SKUs with available > 0
  const onHandByLoc = {}; // locName -> sum of availableUnits (for tooltip)
  for (const b of (data?.inv?.breakdowns || [])) {
    const name = b.dimensions[0]?.label || b.dimensions[0]?.key || '';
    const set = new Set();
    let sum = 0;
    for (const node of (b.items?.nodes || [])) {
      if ((node.availableUnits || 0) > 0) {
        set.add(node.sku);
        sum += node.availableUnits;
      }
    }
    stockedByLoc[name] = set;
    onHandByLoc[name] = sum;
  }
  const unionSize = (names) => {
    const u = new Set();
    for (const n of names) for (const sku of (stockedByLoc[n] || [])) u.add(sku);
    return u.size;
  };
  const storeLocs = Object.keys(stockedByLoc).filter(n => /^\d{3}\s*-/.test(n));
  // Per-store breakdown for the tooltip, sorted by available qty DESC
  const storeBreakdown = storeLocs
    .map(name => ({ name, available: onHandByLoc[name] || 0, skuCount: stockedByLoc[name]?.size || 0 }))
    .filter(s => s.available > 0)
    .sort((a, b) => b.available - a.available);
  return {
    expectedCount,
    Ecomm: unionSize(['FG - Ecomm']),
    Retail: unionSize(['FG - Retail']),
    Wholesale: unionSize(['FG - Wholesale']),
    onHold: unionSize(['Geodis Dallas - On Hold']),
    stores: unionSize(storeLocs),
    storeBreakdown,
  };
}

// Returns { Ecomm: boolean, Retail: boolean, Wholesale: boolean } (legacy shape)
// TRUE = every expected variant has available > 0 at that FG channel.
export async function fetchCcSizeCoverage(ccCode, { signal } = {}) {
  if (!ccCode) return {};
  const data = await cortexQuery(`{
    cw: colorway(by: { id: ${JSON.stringify(ccCode)} }) {
      variants { sku }
    }
    inv: inventory(
      groupBy: LOCATION,
      first: 10,
      filter: { colorwayIds: ${JSON.stringify([ccCode])}, locations: ["FG - Retail", "FG - Ecomm", "FG - Wholesale"] }
    ) {
      breakdowns {
        dimensions { key label }
        items(first: 500) {
          nodes { sku availableUnits }
        }
      }
    }
  }`, { signal });

  const expectedSkus = (data?.cw?.variants || []).map(v => v.sku).filter(Boolean);
  if (expectedSkus.length === 0) return {};

  const invBySku = {};
  for (const b of (data?.inv?.breakdowns || [])) {
    const name = b.dimensions[0]?.label || b.dimensions[0]?.key || '';
    const channel = name.replace(/^FG - /, '');
    for (const node of (b.items?.nodes || [])) {
      if (!invBySku[node.sku]) invBySku[node.sku] = {};
      invBySku[node.sku][channel] = (invBySku[node.sku][channel] || 0) + (node.availableUnits || 0);
    }
  }
  const coverage = {};
  for (const channel of ['Ecomm', 'Retail', 'Wholesale']) {
    coverage[channel] = expectedSkus.every(sku => ((invBySku[sku] || {})[channel] || 0) > 0);
  }
  return coverage;
}

// Replaces rpc('get_inventory_by_cc_for_pos') + adds Geodis On Hold + per-channel SKU coverage.
// Returns:
// { [ccCode]: {
//     onHand, available,
//     byChannel: { E, R, W },           // on-hand per FG channel
//     skuCountByChannel: { E, R, W },   // # of distinct SKUs with inventory rows per channel
//     onHold,                            // on-hand at Geodis Dallas - On Hold
//     onHoldSkuCount,                    // # of SKUs at On Hold
//     catalogSkuCount,                   // # of variants from the Cortex colorway catalog
//   } }
export async function fetchInventoryByCC({ signal } = {}) {
  // Kick off catalog map in parallel so we can use real variant counts as the
  // SKU-coverage denominator (matches what the modal's fetchCcCoverage uses).
  const catalogPromise = fetchCortexImageMap({ signal });
  const data = await cortexQuery(`{
    total: inventory(groupBy: COLORWAY, first: 5000, filter: { channels: ["FG - Ecomm", "FG - Retail", "FG - Wholesale", "FG - Corp Sales", "FG - Marketing"] }) {
      breakdowns {
        dimensions { key }
        metrics { onHandUnits availableUnits skuCount }
      }
    }
    ecomm: inventory(groupBy: COLORWAY, first: 5000, filter: { channels: ["FG - Ecomm"] }) {
      breakdowns { dimensions { key } metrics { onHandUnits skuCount } }
    }
    retail: inventory(groupBy: COLORWAY, first: 5000, filter: { channels: ["FG - Retail"] }) {
      breakdowns { dimensions { key } metrics { onHandUnits skuCount } }
    }
    whsl: inventory(groupBy: COLORWAY, first: 5000, filter: { channels: ["FG - Wholesale"] }) {
      breakdowns { dimensions { key } metrics { onHandUnits skuCount } }
    }
    onHold: inventory(groupBy: COLORWAY, first: 5000, filter: { locations: ["Geodis Dallas - On Hold"] }) {
      breakdowns { dimensions { key } metrics { onHandUnits skuCount } }
    }
  }`, { signal });

  await catalogPromise;

  return buildInventoryByCCMap(data, getCortexVariantCountSync);
}

// Pure: turns the five Cortex inventory breakdowns fetched by fetchInventoryByCC
// into the per-CC map. Split out so it can be tested without the network.
export function buildInventoryByCCMap(data, catalogCountFor = () => 0) {
  const map = {};
  for (const b of (data?.total?.breakdowns || [])) {
    const cc = b.dimensions[0]?.key;
    if (!cc) continue;
    map[cc] = {
      onHand: b.metrics.onHandUnits || 0,
      available: b.metrics.availableUnits || 0,
      byChannel: { E: 0, R: 0, W: 0 },
      skuCountByChannel: { E: 0, R: 0, W: 0 },
      onHold: 0,
      onHoldSkuCount: 0,
      // Real catalog size from Cortex colorway variants — same denominator the
      // modal uses — with a fallback to the inventory-row skuCount proxy.
      catalogSkuCount: catalogCountFor(cc) || b.metrics.skuCount || 0,
    };
  }
  const applyChannel = (key, prop) => {
    for (const b of (data?.[key]?.breakdowns || [])) {
      const cc = b.dimensions[0]?.key;
      if (!cc || !map[cc]) continue;
      map[cc].byChannel[prop] = b.metrics.onHandUnits || 0;
      map[cc].skuCountByChannel[prop] = b.metrics.skuCount || 0;
    }
  };
  applyChannel('ecomm', 'E');
  applyChannel('retail', 'R');
  applyChannel('whsl', 'W');

  for (const b of (data?.onHold?.breakdowns || [])) {
    const cc = b.dimensions[0]?.key;
    if (!cc) continue;
    // A freshly received launch can sit entirely at Geodis On Hold with no
    // put-away FG stock yet, so it has no `total` row. Give it an entry anyway,
    // or Launch Line Up drops its On Hold row and shows dashes everywhere.
    if (!map[cc]) {
      map[cc] = {
        onHand: 0,
        available: 0,
        byChannel: { E: 0, R: 0, W: 0 },
        skuCountByChannel: { E: 0, R: 0, W: 0 },
        onHold: 0,
        onHoldSkuCount: 0,
        catalogSkuCount: catalogCountFor(cc) || b.metrics.skuCount || 0,
      };
    }
    map[cc].onHold = b.metrics.onHandUnits || 0;
    map[cc].onHoldSkuCount = b.metrics.skuCount || 0;
  }
  return map;
}

// green (full) / rust (partial) / red (none) — matches boot color logic
export function inventoryStatusColor(stockedSkus, catalogSkus, B) {
  if (!catalogSkus) return B.stone;
  if (stockedSkus === 0) return STOCK_RED;
  if (stockedSkus >= catalogSkus) return B.olive;
  return B.rust;
}

// ─── Boot icon (uses /cartwright-outline-right.svg as CSS mask) ──────────────

const BOOT_ASPECT = 378 / 427;

export const BootIcon = React.memo(function BootIcon({ color, size = 16, title }) {
  return (
    <span
      role="img"
      aria-label={title || 'boot'}
      title={title}
      style={{
        display: 'inline-block',
        width: size,
        height: size / BOOT_ASPECT,
        backgroundColor: color,
        maskImage: 'url(/cartwright-outline-right.svg)',
        WebkitMaskImage: 'url(/cartwright-outline-right.svg)',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        flexShrink: 0,
      }}
    />
  );
});
